import { useState, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { workspaceService, WorkspaceService } from '../services/workspaceService';
import { debugLog, debugError } from '@/lib/debugUtils';
import type { 
  Workspace, 
  CreateWorkspaceParams, 
  CreateWorkspaceResult, 
  WorkspaceError, 
  UseWorkspaceReturn 
} from '../types/workspace';
// Import the new cluster interface
import type { KeywordCluster } from '@/shared/types/keyword-types';
import { supabase } from '@/integrations/supabase/client';

/**
 * Enhanced workspace management hook with comprehensive state management
 * Following React best practices and Software Architect patterns
 * 
 * Updated to include cluster data fetching with enhanced fields from schema migration
 * 
 * Features:
 * - State management for loading and error states
 * - Integration with authentication context
 * - Comprehensive error handling with user-friendly messages
 * - Memory management and cleanup
 * - Optimized re-renders with useCallback
 * - Type safety throughout
 * - Enhanced cluster data fetching with new schema fields
 * 
 * @returns Enhanced workspace management interface
 */
export function useWorkspace(): UseWorkspaceReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<WorkspaceError | null>(null);
  const [lastCreatedWorkspace, setLastCreatedWorkspace] = useState<Workspace | null>(null);
  
  const { isAuthenticated, userId, isLoading: authLoading, user } = useAuth();
  
  // Get user email for debug logging
  const userEmail = user?.email || null;
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Cleanup abort controller on unmount or new operations
   */
  const cleanupAbortController = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  /**
   * Handle workspace operation with consistent error handling pattern
   */
  const handleWorkspaceOperation = useCallback(async <T>(
    operation: () => Promise<T>,
    operationName: string
  ): Promise<T> => {
    // Enhanced authentication check with detailed logging
    console.log(`[useWorkspace] ${operationName} - Auth state:`, {
      isAuthenticated,
      userId,
      authLoading
    });

    if (authLoading) {
      const authError: WorkspaceError = {
        type: 'authentication_error' as const,
        message: 'Authentication is still loading. Please wait.'
      };
      setError(authError);
      throw authError;
    }

    if (!isAuthenticated) {
      const authError: WorkspaceError = {
        type: 'authentication_error' as const,
        message: 'Authentication required for workspace operations'
      };
      setError(authError);
      throw authError;
    }

    if (!userId) {
      const authError: WorkspaceError = {
        type: 'authentication_error' as const,
        message: 'User ID is required for workspace operations'
      };
      setError(authError);
      throw authError;
    }

    // Setup operation
    setIsLoading(true);
    setError(null);
    cleanupAbortController();
    abortControllerRef.current = new AbortController();

    try {
      debugLog(userEmail, `[useWorkspace] Starting ${operationName} for user:`, userId);
      const result = await operation();
      debugLog(userEmail, `[useWorkspace] ${operationName} completed successfully`);
      return result;
    } catch (err) {
      debugError(userEmail, `[useWorkspace] Error in ${operationName}:`, err);
      
      // Handle workspace errors with operation-specific messages
      if (err && typeof err === 'object' && 'type' in err) {
        const workspaceError = err as WorkspaceError;
        // Override generic database error messages based on operation
        if (workspaceError.type === 'database_error') {
          if (operationName.toLowerCase().includes('fetch') || operationName.toLowerCase().includes('get')) {
            workspaceError.message = 'Failed to fetch workspaces. Please try again.';
          } else if (operationName.toLowerCase().includes('create')) {
            workspaceError.message = 'Failed to create workspace. Please try again.';
          }
        }
        setError(workspaceError);
        throw workspaceError;
      }

      // Handle abort errors (user cancelled operation)
      if (err instanceof Error && err.name === 'AbortError') {
        debugLog(userEmail, `[useWorkspace] ${operationName} was cancelled`);
        return Promise.reject(err);
      }

      // Generic error fallback
      const genericError: WorkspaceError = {
        type: 'unknown_error' as const,
        message: err instanceof Error ? err.message : `Failed to ${operationName.toLowerCase()}`
      };
      setError(genericError);
      throw genericError;
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, [isAuthenticated, userId, authLoading, cleanupAbortController]);

  /**
   * Create a new workspace with enhanced tracking
   */
  const createWorkspace = useCallback(async (params: CreateWorkspaceParams): Promise<CreateWorkspaceResult> => {
    return handleWorkspaceOperation(async () => {
      // userId is guaranteed to be available due to auth check in handleWorkspaceOperation
      const result = await workspaceService.createWorkspace(params, userId!, userEmail);
      
      if (result.success && result.workspace) {
        setLastCreatedWorkspace(result.workspace);
        debugLog(userEmail, '[useWorkspace] Workspace created and tracked:', result.workspace.id);
      }
      
      return result;
    }, 'Create Workspace');
  }, [handleWorkspaceOperation, userId, userEmail]);

  /**
   * Get all user workspaces
   */
  const getUserWorkspaces = useCallback(async (): Promise<Workspace[]> => {
    return handleWorkspaceOperation(async () => {
      // userId is guaranteed to be available due to auth check in handleWorkspaceOperation
      return await workspaceService.getUserWorkspaces(userId!, false, userEmail);
    }, 'Get User Workspaces');
  }, [handleWorkspaceOperation, userId, userEmail]);

  /**
   * Get keyword clusters for a workspace with enhanced fields
   * NEW: Fetches cluster data with all enhanced schema fields
   */
  const getWorkspaceKeywordClusters = useCallback(async (workspaceId: string): Promise<KeywordCluster[]> => {
    return handleWorkspaceOperation(async () => {
      if (!userId || !workspaceId) {
        throw new Error('User ID and workspace ID are required for cluster fetching');
      }

      debugLog(userEmail, '[useWorkspace] Fetching keyword clusters with enhanced fields for workspace:', workspaceId);

      // Fetch clusters with all enhanced fields from schema migration
      const { data, error } = await supabase
        .from('keyword_clusters')
        .select(`
          *,
          secondary_keywords,
          related_terms,
          semantic_variations,
          question_variations,
          keyword_integration_notes,
          content_type,
          category
        `)
        .eq('workspace_id', workspaceId)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        debugError(userEmail, '[useWorkspace] Error fetching keyword clusters:', error);
        throw new Error(`Failed to fetch keyword clusters: ${error.message}`);
      }

      debugLog(userEmail, '[useWorkspace] Keyword clusters fetched:', data?.length || 0, 'records');
      debugLog(userEmail, '[useWorkspace] Sample cluster with enhanced fields:', data?.[0]);

      return (data || []) as KeywordCluster[];
    }, 'Get Workspace Keyword Clusters');
  }, [handleWorkspaceOperation, userId, userEmail]);

  /**
   * Clear current error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Get user-friendly error message with operation-specific context
   */
  const getErrorMessage = useCallback((): string | null => {
    if (!error) return null;
    
    // Custom error messages for specific cases
    const customMessages: Partial<Record<WorkspaceError['type'], string>> = {
      authentication_error: 'Please log in to access your workspaces.',
      database_error: error.message, // Use the specific message set in handleWorkspaceOperation
      network_error: 'Network connection failed. Please check your internet connection and try again.',
      unknown_error: 'An unexpected error occurred. Please refresh the page and try again.'
    };
    
    return customMessages[error.type] || WorkspaceService.getErrorMessage(error);
  }, [error]);

  return {
    createWorkspace,
    getUserWorkspaces,
    getWorkspaceKeywordClusters, // NEW: Added cluster fetching method
    isLoading,
    error,
    clearError,
    getErrorMessage,
    lastCreatedWorkspace
  };
}

/**
 * Hook specifically for workspace creation with optimized state management
 * Useful for components that only need creation functionality
 */
export function useWorkspaceCreation() {
  const { createWorkspace, isLoading, error, clearError, getErrorMessage, lastCreatedWorkspace } = useWorkspace();
  
  return {
    createWorkspace,
    isLoading,
    error,
    clearError,
    getErrorMessage,
    lastCreatedWorkspace
  };
}

/**
 * Hook for workspace listing with caching capabilities
 * Useful for workspace management pages
 */
export function useWorkspaceList() {
  const { getUserWorkspaces, getWorkspaceKeywordClusters, isLoading, error, clearError, getErrorMessage } = useWorkspace();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [lastFetchTime, setLastFetchTime] = useState<Date | null>(null);

  const fetchWorkspaces = useCallback(async (forceRefresh = false) => {
    // Simple caching: don't refetch if we have data and it's less than 5 minutes old
    if (!forceRefresh && workspaces.length > 0 && lastFetchTime) {
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
      if (lastFetchTime > fiveMinutesAgo) {
        console.log('[useWorkspaceList] Using cached workspaces');
        return workspaces;
      }
    }

    try {
      const fetchedWorkspaces = await getUserWorkspaces();
      setWorkspaces(fetchedWorkspaces);
      setLastFetchTime(new Date());
      return fetchedWorkspaces;
    } catch (error) {
      console.error('[useWorkspaceList] Error fetching workspaces:', error);
      throw error;
    }
  }, [getUserWorkspaces, workspaces, lastFetchTime]);

  const refreshWorkspaces = useCallback(() => {
    return fetchWorkspaces(true);
  }, [fetchWorkspaces]);

  return {
    workspaces,
    fetchWorkspaces,
    refreshWorkspaces,
    getWorkspaceKeywordClusters, // NEW: Expose cluster fetching
    isLoading,
    error,
    clearError,
    getErrorMessage,
    lastFetchTime
  };
}